import { AccountInfo, PublicClientApplication } from "@azure/msal-browser";
import { AuthSource } from "@granular/fabric3-definitions";

const PHOTO_LOCALSTORAGE_PREFIX = "sso-photo" as const;
const PHOTO_CACHE_DURATION = 1000 * 60 * 60 * 24 * 7; // 1 week

const GRAPH_PHOTO_URL = "https://graph.microsoft.com/v1.0/me/photo/$value";

type CachedPhoto = {
  timestamp: number;
  dataURL: string;
};

const GRAPH_CAPABLE_AUTH_SOURCES = new Set<AuthSource>(["b2b", "vylor_b2b"]);

export const canFetchGraphPhoto = (authSource: AuthSource): boolean =>
  GRAPH_CAPABLE_AUTH_SOURCES.has(authSource);

const dataURLToBlob = async (dataURL: string): Promise<Blob | undefined> => {
  try {
    const res = await fetch(dataURL);
    return await res.blob();
  } catch (error) {
    console.error("Failed to convert data URL to blob:", error);
    return undefined;
  }
};

const blobToDataURL = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (reader.result && typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject(new Error("Failed to convert blob to data URL"));
      }
    };
    reader.readAsDataURL(blob);
  });
};

const makeLocalStorageKey = (account: AccountInfo) => {
  return `${PHOTO_LOCALSTORAGE_PREFIX}-${account.username}-${account.tenantId}`;
};

const isImageBlob = (blob: Blob): boolean => blob.type.startsWith("image/");

const loadPhotoBlobFromLocalStorage = async (
  account: AccountInfo,
): Promise<Blob | undefined> => {
  const stored = localStorage.getItem(makeLocalStorageKey(account));
  if (!stored) return undefined;
  try {
    const cached = JSON.parse(stored) as CachedPhoto;
    if (Date.now() - cached.timestamp > PHOTO_CACHE_DURATION) {
      localStorage.removeItem(makeLocalStorageKey(account));
      return undefined;
    }
    const blob = await dataURLToBlob(cached.dataURL);
    if (!blob) return undefined;
    if (!isImageBlob(blob)) {
      // Written by an older build that cached an error body. Drop it so we re-fetch.
      console.warn(
        `Discarding cached PFP with unexpected type "${blob.type}", re-fetching.`,
      );
      localStorage.removeItem(makeLocalStorageKey(account));
      return undefined;
    }
    return blob;
  } catch (error) {
    console.error("Failed to load cached PFP:", error);
  }
  return undefined;
};

export const savePhotoBlobToLocalStorage = async (
  account: AccountInfo,
  blob: Blob,
): Promise<void> => {
  if (!isImageBlob(blob)) {
    throw new Error(
      `Refusing to cache non-image PFP blob (type "${blob.type}")`,
    );
  }
  const dataUrl = await blobToDataURL(blob);
  const cached = {
    timestamp: Date.now(),
    dataURL: dataUrl,
  };
  localStorage.setItem(makeLocalStorageKey(account), JSON.stringify(cached));
};

/**
 * Get a graph access token for the active account on the given authApp.
 * This can be used to fetch a profile photo or other user info from Microsoft Graph.
 * @throws InteractionRequiredAuthError, NoAccountError
 * @returns
 */
export const getGraphAccessToken = async (
  authApp: PublicClientApplication,
  account: AccountInfo,
): Promise<string> => {
  const request = {
    scopes: ["User.Read", "User.ReadBasic.All"],
    account,
    state: JSON.stringify({ isGraphRequest: true }),
  };
  const token = await authApp.acquireTokenSilent(request);
  return token.accessToken;
};

/**
 * Pulls a profile photo from Microsoft Graph for the active account on the
 * given authApp, caching it in localStorage for up to a week.
 *
 * Set the returned URL as the `src` of an `<img>` element to display it. A missing avatar is a
 * normal outcome (no photo uploaded, B2C account, Graph unreachable), so this resolves to
 * `undefined` rather than rejecting — callers should render a fallback.
 *
 * @param authApp PublicClientApplication. Uses the active account.
 * @param authSource Which tenant the account came from. Graph is B2B-only.
 * @returns A URL to the photo blob, or undefined if no photo could be retrieved
 */
export const getPhotoURL = async (
  authApp: PublicClientApplication,
  authSource: AuthSource,
): Promise<string | undefined> => {
  if (!canFetchGraphPhoto(authSource)) return undefined;

  const account = authApp.getActiveAccount();
  if (!account) {
    console.warn("No active account in authApp, skipping PFP fetch.");
    return undefined;
  }

  const storedBlob = await loadPhotoBlobFromLocalStorage(account);
  if (storedBlob) {
    return URL.createObjectURL(storedBlob);
  }

  try {
    const graphAccessToken = await getGraphAccessToken(authApp, account);
    const photoResponse = await fetch(GRAPH_PHOTO_URL, {
      headers: {
        Authorization: `Bearer ${graphAccessToken}`,
      },
    });

    if (!photoResponse.ok) {
      console.warn(
        `Graph PFP fetch failed with ${photoResponse.status}, using fallback.`,
      );
      return undefined;
    }

    const photoBlob = await photoResponse.blob();
    if (!isImageBlob(photoBlob)) {
      console.warn(
        `Graph PFP response was not an image (type "${photoBlob.type}"), using fallback.`,
      );
      return undefined;
    }

    try {
      await savePhotoBlobToLocalStorage(account, photoBlob);
    } catch (error) {
      // Quota exceeded or encoding failed. The photo still renders this session; we just don't cache it.
      console.warn("Failed to cache PFP, continuing without cache:", error);
    }
    return URL.createObjectURL(photoBlob);
  } catch (error) {
    console.warn("Failed to fetch PFP from Graph, using fallback:", error);
    return undefined;
  }
};
